import { Component } from '@theme/component';

/**
 * Enhances a native mobile product rail without duplicating its desktop cards.
 * @extends {Component}
 */
class RealProductShelf extends Component {
  requiredRefs = ['track'];

  #mobileQuery = window.matchMedia('(max-width: 749px)');
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #resizeObserver;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.setAttribute('data-enhanced', '');
    this.refs.track.addEventListener('scroll', this.#scheduleUpdate, { passive: true });
    this.addEventListener('focusin', this.#handleFocusIn);
    this.#mobileQuery.addEventListener('change', this.#handleMediaChange);
    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(this.refs.track);
    this.#handleMediaChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.track.removeEventListener('scroll', this.#scheduleUpdate);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.#mobileQuery.removeEventListener('change', this.#handleMediaChange);
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  previous() {
    this.#scroll(-1);
  }

  next() {
    this.#scroll(1);
  }

  #handleMediaChange = () => {
    if (!this.#mobileQuery.matches) {
      this.refs.track.scrollTo({ left: 0, top: 0, behavior: 'instant' });
    }
    this.#scheduleUpdate();
  };

  #scheduleUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      if (this.isConnected) this.#updateControls();
    });
  };

  #scroll(direction) {
    if (!this.#mobileQuery.matches) return;

    const track = this.refs.track;
    const first = track.firstElementChild;
    if (!first || !track.clientWidth) return;

    const style = getComputedStyle(track);
    const gap = parseFloat(style.columnGap) || 0;
    const step = first.getBoundingClientRect().width + gap;
    const available = track.clientWidth - (parseFloat(style.paddingInlineStart) || 0)
      - (parseFloat(style.paddingInlineEnd) || 0);
    const visibleCount = Math.max(1, Math.floor((available + gap) / step));

    track.scrollBy({
      left: direction * (style.direction === 'rtl' ? -1 : 1) * visibleCount * step,
      behavior: this.#reducedMotionQuery.matches ? 'auto' : 'smooth',
    });
  }

  #handleFocusIn = (event) => {
    if (!this.#mobileQuery.matches || !(event.target instanceof Element)) return;

    const track = this.refs.track;
    const item = event.target.closest('.real-product-shelf__item');
    if (!item || item.parentElement !== track) return;

    const style = getComputedStyle(track);
    const bounds = track.getBoundingClientRect();
    const itemBounds = item.getBoundingClientRect();
    const left = bounds.left + (parseFloat(style.paddingLeft) || 0);
    const right = bounds.right - (parseFloat(style.paddingRight) || 0);
    const delta = itemBounds.left < left ? itemBounds.left - left
      : itemBounds.right > right ? itemBounds.right - right : 0;

    if (delta) track.scrollBy({ left: delta, behavior: 'instant' });
  };

  #updateControls() {
    const track = this.refs.track;
    const arrows = this.querySelector(':scope > .real-product-shelf__arrows');
    if (!arrows) return;

    const hasOverflow = track.scrollWidth - track.clientWidth > 2;
    arrows.hidden = !this.#mobileQuery.matches || !track.clientWidth || !hasOverflow;
    if (arrows.hidden) return;

    const first = track.firstElementChild;
    const last = track.lastElementChild;
    if (!first || !last) return;

    const style = getComputedStyle(track);
    const rtl = style.direction === 'rtl';
    const bounds = track.getBoundingClientRect();
    const start = rtl ? bounds.right - (parseFloat(style.paddingInlineStart) || 0)
      : bounds.left + (parseFloat(style.paddingInlineStart) || 0);
    const end = rtl ? bounds.left + (parseFloat(style.paddingInlineEnd) || 0)
      : bounds.right - (parseFloat(style.paddingInlineEnd) || 0);
    const firstBounds = first.getBoundingClientRect();
    const lastBounds = last.getBoundingClientRect();

    if (this.refs.previous) {
      this.refs.previous.disabled = rtl ? firstBounds.right <= start + 2 : firstBounds.left >= start - 2;
    }
    if (this.refs.next) {
      this.refs.next.disabled = rtl ? lastBounds.left >= end - 2 : lastBounds.right <= end + 2;
    }
  }
}

if (!customElements.get('real-product-shelf')) {
  customElements.define('real-product-shelf', RealProductShelf);
}
